import Icon from '@/components/ui/AppIcon';
import LiveSoldCount from '@/components/common/LiveSoldCount';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { supabaseBrowser } from '@/lib/supabaseBrowser';
import { useAuth } from '@/contexts/AuthContext';

const supabase = supabaseBrowser;

const CompetitionInfo = ({ competition, instantWinPrizes = [] }) => {
  const [prizes, setPrizes] = useState(instantWinPrizes);
  const { user } = useAuth();

  // Instant-win prizes come from get_instant_win_prizes_public(): every prize's
  // amount and won/available state, but the ticket number and winner's name
  // only once it has been won, so unwon winning numbers are never sent to a
  // player's browser. Admins also get every ticket number and the winner's
  // account (winnerId), so those are links to the customer's page. Players
  // can't subscribe to the prize table any more, so poll every 30 seconds,
  // and again when someone signs in.
  useEffect(() => {
    if (competition?.instant_win_type !== 'instant_win' || !competition?.id) return undefined;

    let cancelled = false;
    const refreshPrizes = async () => {
      try {
        const { data, error } = await supabase?.rpc('get_instant_win_prizes_public', {
          p_competition_id: competition?.id
        });
        if (error) throw error;
        if (!cancelled) {
          setPrizes(
            (data || []).map((prize) => ({
              id: prize?.id,
              ticketNumber: prize?.ticket_number ?? null,
              prizeAmount: Number(prize?.prize_amount) || 0,
              isClaimed: !!prize?.is_claimed,
              claimedAt: prize?.claimed_at ?? null,
              winnerName: prize?.winner_name ?? null,
              winnerId: prize?.winner_id ?? null
            }))
          );
        }
      } catch (err) {
        console.error('Error refreshing instant win prizes:', err);
      }
    };

    refreshPrizes();
    const interval = setInterval(refreshPrizes, 30000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [competition?.id, competition?.instant_win_type, user?.id]);

  // For admins: the winner's name or ticket number, linked to their page.
  const toCustomer = (prize, children) =>
    prize?.winnerId ? (
      <Link
        href={`/user-management/${prize.winnerId}`}
        className="underline underline-offset-2 hover:text-accent"
      >
        {children}
      </Link>
    ) : (
      children
    );

  const formatCurrency = (amount) => {
    // Add validation to ensure amount is a valid number
    const numAmount = Number(amount);
    if (!amount || isNaN(numAmount)) {
      return '£0.00';
    }
    // Whole pounds without pence (£1,400); prices keep them (£1.99).
    const whole = Number.isInteger(numAmount);
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2
    })?.format(numAmount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-GB')?.format(num);
  };

  return (
    <div>
      {/* The title, tagline, price, countdown and draw result are in the
          page's heading band; this panel is the detail underneath. */}
      {competition?.description && (
        <div className="max-w-[68ch]">
          <h2 className="d text-[length:var(--t-h3)]">About the prize</h2>
          <p className="mt-4 text-[1.05rem] leading-[1.65] text-foreground/85 whitespace-pre-line">
            {competition?.description}
          </p>
        </div>
      )}

      {/* No prize value here (Rob): it isn't shown on the site. */}
      <dl className="mt-8 grid grid-cols-3 border-y border-white/[0.12]">
        {[
          ['Ticket price', formatCurrency(competition?.ticketPrice)],
          ['Tickets', formatNumber(competition?.totalTickets)],
          [
            'Sold',
            <LiveSoldCount
              key="sold"
              competitionId={competition?.id}
              initialSold={competition?.ticketsSold || 0}
              totalTickets={competition?.totalTickets || 0}
            >
              {(n) => formatNumber(n)}
            </LiveSoldCount>,
          ],
        ].map(([label, value], i) => (
          <div
            key={label}
            className={`py-4 pr-4 ${i ? 'pl-4 border-l border-white/[0.12]' : ''}`}
          >
            <dt className="text-xs font-bold uppercase tracking-[0.1em] text-muted-foreground">{label}</dt>
            <dd className="num mt-2 text-[1.6rem]">{value}</dd>
          </div>
        ))}
      </dl>

      {/* Bundle prices: the more tickets, the less each one costs. On phones
          the Pick your numbers tab already shows them, so not here (Rob). */}
      {Array.isArray(competition?.bulkPricingTiers) && competition.bulkPricingTiers.length > 0 && (() => {
        const base = parseFloat(competition?.ticketPrice) || 0;
        const sortedTiers = [...competition.bulkPricingTiers]
          .map(t => ({ ...t, minQuantity: parseInt(t?.minQuantity, 10) || 0 }))
          .sort((a, b) => a.minQuantity - b.minQuantity);
        return (
          <div className="mt-10 hidden lg:block">
            <h3 className="d text-[length:var(--t-h3)]">Bundle prices</h3>
            <p className="mt-2 text-muted-foreground">The more tickets you take, the less each one costs.</p>
            <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
              {sortedTiers.map((tier, idx) => {
                let price = base;
                let pct = 0;
                if (tier?.discountPercent !== undefined && tier?.discountPercent !== null) {
                  pct = Math.max(0, Math.min(100, parseFloat(tier.discountPercent) || 0));
                  price = Number((base * (1 - pct / 100)).toFixed(2));
                } else if (tier?.pricePerTicket !== undefined && tier?.pricePerTicket !== null) {
                  price = parseFloat(tier.pricePerTicket) || 0;
                  if (base > 0) pct = ((base - price) / base) * 100;
                }
                const pctRounded = Math.round(pct);
                return (
                  <li
                    key={idx}
                    className="flex items-center justify-between gap-4 rounded-[8px] border border-white/[0.12] bg-[#111612] px-4 py-3"
                  >
                    <span>
                      <span className="block font-bold">{tier.minQuantity}+ tickets</span>
                      <span className="text-sm text-muted-foreground tabular-nums">
                        {formatCurrency(price)} each
                      </span>
                    </span>
                    {pctRounded > 0 && (
                      <span className="rounded-[4px] bg-accent px-2 py-1 text-xs font-black text-accent-foreground">
                        Save {pctRounded}%
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })()}

      {/* NEW: Instant Win Prizes Section - ONLY for instant win competitions */}
      {(competition?.instant_win_type === 'instant_win' && prizes?.length > 0) && (
        <div className="mt-10 border-t border-white/[0.12] pt-8">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="GiftIcon" size={24} className="text-accent" />
            <h3 className="text-xl font-bold text-card-foreground">
              Instant Win Prizes
            </h3>
          </div>
          
          <div className="bg-accent/5 backdrop-blur-sm border border-accent/20 rounded-[8px] p-4 mb-4">
            <p className="text-sm text-muted-foreground">
              This is an instant win competition! Purchase tickets for a chance to instantly win one of these prizes. 
              When you buy a ticket, you'll immediately know if you've won! Every ticket is also in the
              draw for the main prize.
            </p>
          </div>

          {prizes?.some((prize) => !prize?.isClaimed && prize?.ticketNumber != null) && (
            <p className="mb-4 text-sm text-muted-foreground">
              You&rsquo;re signed in as an admin, so you can see every prize&rsquo;s ticket number
              (dashed). Players only see a number once it has been won.
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {prizes?.map((prize) => (
              <div
                key={prize?.id}
                className={`rounded-[8px] border-2 p-4 transition-all duration-300 hover:shadow-lg backdrop-blur-sm ${
                  prize?.isClaimed
                    ? 'border-error/30 bg-error/5' : 'border-success/30 bg-success/5'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        prize?.isClaimed ? 'bg-error' : 'bg-success animate-pulse-subtle'
                      }`}
                    ></div>
                    <span
                      className={`text-xs font-semibold uppercase tracking-wide ${
                        prize?.isClaimed ? 'text-error' : 'text-success'
                      }`}
                    >
                      {prize?.isClaimed ? 'Won' : 'Available'}
                    </span>
                  </div>
                  {prize?.ticketNumber != null && (
                    <span
                      className={`text-xs font-medium text-muted-foreground bg-muted/50 px-2 py-1 rounded-full ${
                        prize?.isClaimed ? '' : 'border border-dashed border-muted-foreground/60'
                      }`}
                    >
                      {toCustomer(prize, `#${prize?.ticketNumber}`)}
                    </span>
                  )}
                </div>

                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground mb-1">
                    {formatCurrency(prize?.prizeAmount|| prize?.prizeAmount || 0)}
                  </p>

                  {prize?.isClaimed && (prize?.winnerName || prize?.claimedAt) && (
                    <div className="mt-2 pt-2 border-t border-error/20 text-xs text-error space-y-0.5">
                      {prize?.winnerName && (
                        <p className="font-semibold">Won by {toCustomer(prize, prize.winnerName)}</p>
                      )}
                      {prize?.claimedAt && (
                        <p>
                          {prize?.winnerName ? 'on' : 'Won on'}{' '}
                          {new Date(prize?.claimedAt)?.toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-card/50 backdrop-blur-sm border border-border rounded-[8px] p-4 shadow-sm">
            <div className="flex items-start space-x-2">
              <Icon name="InformationCircleIcon" size={20} className="text-accent mt-0.5 flex-shrink-0" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium mb-1 text-foreground">How Instant Win Works:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Each ticket number has a predetermined outcome</li>
                  <li>When you purchase a ticket, you'll instantly know if you've won</li>
                  <li>Prize amounts are automatically credited to your wallet</li>
                  <li>Multiple prizes can be won - each ticket is independent</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

CompetitionInfo.propTypes = {
  competition: PropTypes?.shape({
    title: PropTypes?.string?.isRequired,
    description: PropTypes?.string?.isRequired,
    prizeValue: PropTypes?.number?.isRequired,
    ticketPrice: PropTypes?.number?.isRequired,
    totalTickets: PropTypes?.number?.isRequired,
    ticketsSold: PropTypes?.number?.isRequired,
    drawDate: PropTypes?.string?.isRequired,
    status: PropTypes?.string?.isRequired,
    views: PropTypes?.number?.isRequired,
    favorites: PropTypes?.number?.isRequired,
    instant_win_type: PropTypes?.string,
    id: PropTypes?.string?.isRequired
  })?.isRequired,
  instantWinPrizes: PropTypes?.arrayOf(
    PropTypes?.shape({
      id: PropTypes?.string?.isRequired,
      ticketNumber: PropTypes?.number,
      prizeAmount: PropTypes?.number?.isRequired,
      isClaimed: PropTypes?.bool?.isRequired,
      claimedBy: PropTypes?.string,
      claimedAt: PropTypes?.string,
      winnerName: PropTypes?.string,
      winnerId: PropTypes?.string,
      createdAt: PropTypes?.string
    })
  )
};

export default CompetitionInfo;
